import ResponsiveImage from "@/components/ResponsiveImage";
import { Link } from "wouter";
import { Phone, Mail, MapPin } from "lucide-react";
import {
  FadeIn,
  StaggerContainer,
  StaggerItem,
} from "@/components/ScrollAnimations";

const uterumLinks = [
  { label: "Allt om Uterum", href: "/uterum/" },
  { label: "Uterum pris", href: "/uterum/pris/" },
  { label: "Alla områden", href: "/uterum/omraden/" },
  { label: "Bygglov", href: "/uterum/bygglov/" },
  { label: "Vinterbonat", href: "/uterum/vinterbonat/" },
  { label: "Glaspartier", href: "/uterum/glaspartier/" },
  { label: "Inspiration", href: "/uterum/inspiration/" },
  { label: "Boyta", href: "/uterum/boyta/" },
  { label: "Uterum vs Orangeri", href: "/uterum/uterum-vs-orangeri/" },
  { label: "Checklista", href: "/uterum/checklista/" },
  { label: "Vanliga frågor", href: "/uterum/fragor/" },
];

const moreLinks = [
  { label: "Om oss", href: "/om-oss/" },
  { label: "Daniel Nystedt", href: "/om-oss/daniel-nystedt/" },
  { label: "Grund", href: "/uterum/grund/" },
  { label: "Tak", href: "/uterum/tak/" },
  { label: "Uppvärmning", href: "/uterum/uppvarmning/" },
  { label: "Kondens", href: "/uterum/kondens/" },
  { label: "Oisolerat", href: "/uterum/oisolerat/" },
  { label: "Storlek", href: "/uterum/storlek/" },
  { label: "Referenser", href: "/referenser/uterum/" },
  { label: "AI-visualisering", href: "/uterum/visualisera/" },
  { label: "Vår fabrik", href: "/uterum/var-fabrik/" },
  { label: "Integritetspolicy", href: "/integritetspolicy/" },
];

const paketLinks = [
  { label: "Våra paket", href: "/uterum/vara-paket/" },
  { label: "Sommar", href: "/uterum/sommar-paket/" },
  { label: "Förlängd Sommar", href: "/uterum/forlangd-sommar-paket/" },
  { label: "Vår och Höst", href: "/uterum/var-host-paket/" },
  { label: "Vinterbonat", href: "/uterum/vinterbonat-paket/" },
  { label: "Bostandard", href: "/uterum/bostandard-paket/" },
  { label: "Boyta", href: "/uterum/boyta-paket/" },
];

const pillarLinks = [
  {
    label: "Bygglov & Regler",
    href: "/uterum/fragor/kategori/bygglov-regler/",
  },
  {
    label: "Konstruktion & Teknik",
    href: "/uterum/fragor/kategori/konstruktion-teknik/",
  },
  { label: "Ekonomi & Pris", href: "/uterum/fragor/kategori/ekonomi/" },
  { label: "Attefallshus", href: "/uterum/fragor/kategori/attefallshus/" },
];

const cityLinks = [
  { label: "Mälardalen", href: "/uterum/malardalen/" },
  { label: "Stockholm", href: "/uterum/stockholm/" },
  { label: "Göteborg", href: "/uterum/goteborg/" },
  { label: "Malmö", href: "/uterum/malmo/" },
  { label: "Uppsala", href: "/uterum/uppsala/" },
  { label: "Västerås", href: "/uterum/vasteras/" },
  { label: "Helsingborg", href: "/uterum/helsingborg/" },
  { label: "Örebro", href: "/uterum/orebro/" },
  { label: "Linköping", href: "/uterum/linkoping/" },
];

export default function Footer() {
  return (
    <footer className="bg-navy text-white/90">
      <div className="container py-10 sm:py-16">
        <StaggerContainer
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6"
          staggerDelay={0.1}
        >
          {/* Brand */}
          <StaggerItem className="col-span-2 md:col-span-2 lg:col-span-1">
            <Link
              href="/"
              className="block mb-4"
              aria-label="Gå till startsidan"
            >
              <ResponsiveImage sizes="(min-width: 1024px) 176px, 151px"
                  src="/manus-storage/logo-header_6798f633.webp"
                  alt="Nordiska Uterum logotyp"
                  className="h-12 sm:h-14 w-auto brightness-0 invert"
                  width={280}
                  height={89}
                  loading="lazy"
                  decoding="async"
                />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-5 sm:mb-6 max-w-sm">
              Nordiska Inglasningar hjälper dig att planera uterum och
              inglasningar. Egen tillverkning i Eskilstuna och erfarenhet sedan
              2012.
            </p>
            <div className="flex flex-col gap-2 sm:gap-2.5 text-sm">
              <a
                href="tel:+46841026111"
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 shrink-0" />
                08-410 26 111
              </a>
              <a
                href="mailto:info@nordiskainglasningar.se"
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4 shrink-0" />
                <span className="truncate">info@nordiskainglasningar.se</span>
              </a>
              <span className="flex items-center gap-2 text-white/70">
                <MapPin className="w-4 h-4 shrink-0" />
                Abramsons väg 2, Eskilstuna
              </span>
            </div>
          </StaggerItem>

          {/* Uterum links */}
          <StaggerItem>
            <h3 className="font-heading font-bold text-white text-xs sm:text-sm mb-3 sm:mb-4 uppercase tracking-wider">
              Uterum
            </h3>
            <nav className="flex flex-col gap-1.5 sm:gap-2">
              {uterumLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs sm:text-sm text-white/70 hover:text-white hover:translate-x-0.5 transition-all duration-200 py-1.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </StaggerItem>

          {/* Paket links */}
          <StaggerItem>
            <h3 className="font-heading font-bold text-white text-xs sm:text-sm mb-3 sm:mb-4 uppercase tracking-wider">
              Våra paket
            </h3>
            <nav className="flex flex-col gap-1.5 sm:gap-2">
              {paketLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs sm:text-sm text-white/70 hover:text-white hover:translate-x-0.5 transition-all duration-200 py-1.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </StaggerItem>

          {/* More links */}
          <StaggerItem>
            <h3 className="font-heading font-bold text-white text-xs sm:text-sm mb-3 sm:mb-4 uppercase tracking-wider">
              Mer om uterum
            </h3>
            <nav className="flex flex-col gap-1.5 sm:gap-2">
              {moreLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs sm:text-sm text-white/70 hover:text-white hover:translate-x-0.5 transition-all duration-200 py-1.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </StaggerItem>

          {/* Kunskapsguider */}
          <StaggerItem>
            <h3 className="font-heading font-bold text-white text-xs sm:text-sm mb-3 sm:mb-4 uppercase tracking-wider">
              Kunskapsguider
            </h3>
            <nav className="flex flex-col gap-1.5 sm:gap-2">
              {pillarLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs sm:text-sm text-white/70 hover:text-white hover:translate-x-0.5 transition-all duration-200 py-1.5"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </StaggerItem>

          {/* City links */}
          <StaggerItem className="col-span-2 sm:col-span-1">
            <h3 className="font-heading font-bold text-white text-xs sm:text-sm mb-3 sm:mb-4 uppercase tracking-wider">
              Uterum i din stad
            </h3>
            <nav className="grid grid-cols-2 sm:grid-cols-1 gap-1.5 sm:gap-2">
              {cityLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-xs sm:text-sm text-white/70 hover:text-white hover:translate-x-0.5 transition-all duration-200 py-1.5"
                >
                  Uterum {link.label}
                </Link>
              ))}
            </nav>
          </StaggerItem>
        </StaggerContainer>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <FadeIn>
          <div className="container py-4 sm:py-5 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 text-xs text-white/70">
            <span>
              &copy; {new Date().getFullYear()} Nordiska Inglasningar. Alla
              rättigheter förbehållna.
            </span>
            <div className="flex items-center gap-4">
              <span>Org.nr: 556753-0802</span>
            </div>
          </div>
        </FadeIn>
      </div>
    </footer>
  );
}
